import { api } from '../utils/api';
import * as XLSX from 'xlsx';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DailySales {
  date: string;
  totalOrders: number;
  totalRevenue: number;
  avgTicket: number;
}

export interface SalesGrowth {
  revenue: number;
  orders: number;
  avgTicket: number;
}

export interface PaymentMethodStat {
  method: string;
  amount: number;
  percentage: number;
  ordersCount: number;
}

export interface FulfillmentStat {
  channel: string;
  amount: number;
  percentage: number;
  ordersCount: number;
}

export interface CategoryStat {
  name: string;
  revenue: number;
  qty: number;
  percentage: number;
}

export interface ReportInsights {
  bestDay: string;
  bestDayRevenue: number;
  peakHour: string;
  peakHourRevenue: number;
  topPayment: string;
  topChannel: string;
}

export interface SalesReport {
  from: string;
  to: string;
  totalOrders: number;
  totalRevenue: number;
  avgTicket: number;
  daily: DailySales[];
  growth?: SalesGrowth;
  byPaymentMethod?: PaymentMethodStat[];
  byFulfillment?: FulfillmentStat[];
  byCategory?: CategoryStat[];
  insights?: ReportInsights;
}

export interface TopProduct {
  id: number;
  name: string;
  totalQty: number;
  totalRevenue: number;
  share?: number;
}

export interface TopProductsReport {
  from: string;
  to: string;
  products: TopProduct[];
}

export interface TopFlavor {
  id: number;
  name: string;
  totalQty: number;
}

export interface TopFlavorsReport {
  from: string;
  to: string;
  flavors: TopFlavor[];
}

export interface HourlySlot {
  hour: number;
  label: string;
  totalOrders: number;
  totalRevenue: number;
  avgTicket?: number;
  isPeak?: boolean;
}

export interface HourlyReport {
  date: string;
  hours: HourlySlot[];
}

export interface PrepAreaStat {
  area: number;
  areaLabel: string;
  totalOrders: number;
  avgMinutes: number;
  minMinutes: number;
  maxMinutes: number;
}

export interface PreparationTimeReport {
  from: string;
  to: string;
  areas: PrepAreaStat[];
}

// ── API calls ──────────────────────────────────────────────────────────────────

export async function getSalesReport(from: string, to: string): Promise<SalesReport> {
  const { data } = await api.get<SalesReport>('/reports/sales', { params: { from, to } });
  return data;
}

export async function getTopProducts(
  from: string,
  to: string,
  limit = 10,
): Promise<TopProductsReport> {
  const { data } = await api.get<TopProductsReport>('/reports/top-products', {
    params: { from, to, limit },
  });
  return data;
}

export async function getTopFlavors(
  from: string,
  to: string,
  limit = 10,
): Promise<TopFlavorsReport> {
  const { data } = await api.get<TopFlavorsReport>('/reports/top-flavors', {
    params: { from, to, limit },
  });
  return data;
}

export async function getHourlyReport(date: string): Promise<HourlyReport> {
  const { data } = await api.get<HourlyReport>('/reports/hourly', { params: { date } });
  return data;
}

export function getExportUrl(from: string, to: string): string {
  // Build URL with token for direct download
  const base = (import.meta as any).env?.VITE_API_URL ?? '';
  return `${base}/api/reports/export?from=${from}&to=${to}`;
}

export async function getPreparationTimeReport(from: string, to: string): Promise<PreparationTimeReport> {
  const { data } = await api.get<PreparationTimeReport>('/reports/preparation-time', { params: { from, to } });
  return data;
}

export async function downloadCsvExport(from: string, to: string): Promise<void> {
  const response = await api.get('/reports/export', {
    params: { from, to },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `ventas_${from}_${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadExcelReport(from: string, to: string): Promise<void> {
  const response = await api.get('/reports/export', {
    params: { from, to },
    responseType: 'text',
  });
  const wb = XLSX.read(response.data, { type: 'string' });
  XLSX.writeFile(wb, `ventas_${from}_${to}.xlsx`);
}

// ── Ingeniería de Menú (Matriz BCG) ──────────────────────────────────────────

export interface MenuEngineeringItem {
  id: number;
  name: string;
  price: number;
  cost: number;
  margin: number;
  marginPct: number;
  quantity: number;
  revenue: number;
  totalProfit: number;
  hasRecipe: boolean;
  quadrant: 'STAR' | 'PLOWHORSE' | 'PUZZLE' | 'DOG';
  quadrantLabel: string;
  quadrantColor: string;
  recommendation: string;
}

export interface MenuEngineeringReport {
  from: string;
  to: string;
  thresholds: {
    avgVolume: number;
    avgMargin: number;
  };
  summary: {
    totalItemsSold: number;
    totalRevenue: number;
    totalProfit: number;
    starsCount: number;
    plowhorsesCount: number;
    puzzlesCount: number;
    dogsCount: number;
  };
  stars: MenuEngineeringItem[];
  plowhorses: MenuEngineeringItem[];
  puzzles: MenuEngineeringItem[];
  dogs: MenuEngineeringItem[];
  items: MenuEngineeringItem[];
}

export async function getMenuEngineering(from: string, to: string): Promise<MenuEngineeringReport> {
  const { data } = await api.get<MenuEngineeringReport>('/reports/menu-engineering', { params: { from, to } });
  return data;
}

// ── Rendimiento de Meseros (Staff KPIs) ────────────────────────────────────────

export interface WaiterPerformance {
  userId: number;
  userName: string;
  role: string;
  ordersCount: number;
  totalSales: number;
  totalTips: number;
  tablesServed: number;
  avgTicket: number;
  rank: number;
  medal: string;
}

export interface WaitersReport {
  from: string;
  to: string;
  waiters: WaiterPerformance[];
  totalStaff: number;
}

export async function getWaitersReport(from: string, to: string): Promise<WaitersReport> {
  const { data } = await api.get<WaitersReport>('/reports/waiters', { params: { from, to } });
  return data;
}

// ── Control y Liquidación de Propinas ─────────────────────────────────────────

export interface TipsReport {
  from: string;
  to: string;
  totalTips: number;
  cashTips: number;
  electronicTips: number;
  totalSettled: number;
  pendingToSettle: number;
  byWaiter: { waiter: string; count: number; amount: number }[];
  settlementsCount: number;
}

export interface TipSettlement {
  id: number;
  amount: number;
  user_id?: number | null;
  settled_by_user_id?: number | null;
  payment_method: string;
  notes?: string | null;
  settled_at: string;
  user?: { id: number; name: string };
  settledByUser?: { id: number; name: string };
}

export async function getTipsReport(from: string, to: string): Promise<TipsReport> {
  const { data } = await api.get<TipsReport>('/reports/tips', { params: { from, to } });
  return data;
}

export async function settleTips(payload: {
  amount: number;
  user_id?: number | null;
  payment_method?: string;
  notes?: string;
}): Promise<any> {
  const { data } = await api.post('/reports/tips/settle', payload);
  return data;
}

export async function getTipSettlements(): Promise<TipSettlement[]> {
  const { data } = await api.get<TipSettlement[]>('/reports/tips/settlements');
  return data;
}

// ── Exportación Contable Avanzada (Excel Fiscal) ──────────────────────────────

export async function downloadAccountingExport(from: string, to: string): Promise<void> {
  const response = await api.get('/reports/export-accounting', {
    params: { from, to },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `libro_contable_ventas_${from}_${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

